import { type Api } from "api-client";
import { observer } from "mobx-react-lite";
import {
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger, cn } from "ui";
import { getMusclesGroups, type ExerciseThumbnail } from "utils";

import { Content } from "src/components/ExerciseComboboxContent";
import { ExerciseComboboxItem } from "src/components/ExerciseComboboxItem";
export { ExerciseComboboxItem } from "src/components/ExerciseComboboxItem";

import { useExerciseSearchContext } from "src/workouts/ExerciseSearchContext";

export interface ExerciseValue {
  id: string;
  name: string;
  videoURL: string;
  images: { url: string }[];
  previewImages: { url: string }[];
  uploadedVideo: { url: string } | null;
  thumbnail: ExerciseThumbnail;
}

type ExercisePreviewSide = ComponentProps<typeof PopoverContent>["side"];

interface ExerciseComboboxProps {
  value?: ExerciseValue | null;
  triggerClassName?: string;
  className?: string;
  valueClassName?: string;
  imageClassName?: string;
  onChange(
    exercise: Api.ListExercises.Output.CurrentPage,
  ): void | Promise<void>;
  trigger?: ReactNode;
  exercisePreviewSide?: ExercisePreviewSide;
  defaultOpen?: boolean;
  exerciseNameClassName?: string;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  autoPlayVideoAfter?: number;
}

export const ExerciseCombobox = observer(function ExerciseCombobox(
  props: ExerciseComboboxProps,
) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false);
  const [loadingExerciseId, setLoadingExerciseId] = useState("");
  const exerciseSearchContext = useExerciseSearchContext();

  const open = props.isOpen ?? isOpen;

  function setOpen(isOpen: boolean) {
    const fn = props.setIsOpen ?? setIsOpen;

    if (!isOpen) {
      exerciseSearchContext.setFilters({
        query: "",
        muscles: [],
      });
    }

    fn(isOpen);
  }

  return (
    <Popover
      defaultOpen={props.defaultOpen}
      open={open}
      onOpenChange={setOpen}
      modal={props.modal ?? true}
    >
      {props.trigger ?? (
        <PopoverTrigger
          disabled={props.disabled}
          role="combobox"
          aria-expanded={open}
          className={cn("min-w-0 w-full capitalize", props.triggerClassName, {
            "text-zinc-400": !props.value,
            "justify-between": !!props.value,
          })}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {props.value ? (
            <ExerciseComboboxItem
              selected={false}
              exercise={props.value}
              imageClassName={cn("h-8 w-8 rounded-[4px]", props.imageClassName)}
              className={cn(props.valueClassName, "truncate")}
              exercisePreviewSide={props.exercisePreviewSide}
              exerciseNameClassName={props.exerciseNameClassName}
              autoPlayVideoAfter={props.autoPlayVideoAfter}
            />
          ) : (
            t("exerciseCombobox.placeholder")
          )}
        </PopoverTrigger>
      )}

      {open && (
        <PopoverContent
          className={cn(
            "p-0 popover-content-width-full pointer-events-auto",
            props.className,
          )}
        >
          <Content
            onChange={async (value) => {
              setLoadingExerciseId(value.id);
              await props.onChange(value);
              setLoadingExerciseId("");
              setOpen(false);
            }}
            value={props.value}
            loadingExerciseId={loadingExerciseId}
            exercisePreviewSide={props.exercisePreviewSide}
            exerciseNameClassName={props.exerciseNameClassName}
            autoPlayVideoAfter={props.autoPlayVideoAfter}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      )}
    </Popover>
  );
});
export interface ContentProps {
  onChange: ExerciseComboboxProps["onChange"];
  value: ExerciseComboboxProps["value"];
  loadingExerciseId?: string;
  exercisePreviewSide?: ExercisePreviewSide;
  exerciseNameClassName?: string;
  autoPlayVideoAfter?: number;
  onClose: () => void;
}
export const comboboxMuscleGroups = getMusclesGroups(
  "Chest",
  "Back",
  "Deltoids",
  "Biceps",
  "Triceps",
  "Abdominals",
  "Lower back",
  "Quadriceps",
  "Glutes",
);
export interface ExerciseComboboxItemProps {
  exercise: ExerciseValue;
  selected: boolean;
  loadingExerciseId?: string;
  imageClassName?: string;
  className?: string;
  popoverAnchorRef?: RefObject<{ getBoundingClientRect(): DOMRect }>;
  exercisePreviewSide?: ComponentProps<typeof PopoverContent>["side"];
  exerciseNameClassName?: string;
  showPreview?: boolean;
  autoPlayVideoAfter?: number;
}
